import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { getEndpoint, getCurrentUser } from 'src/app/core/services/functions.service';

export interface ExcelRow {
  c_vend: string;
  fecha_inicio: string | Date;
  fecha_fin: string | Date;
  valor: string | number;
}

export interface Garantizado {
  id?: number;
  c_emp: string;
  c_vend: string;
  valor: number;
  fecha_inicio: Date;
  fecha_fin: Date;
  usuario_creacion?: string;
  fecha_creacion?: string;
  usuario_modificacion?: string;
  fecha_modificacion?: string;
  estado?: string;
  nombre_vendedor?: string;
  vendedor?: {
    codigo_vendedor: string;
    nombre_vendedor: string;
    estado: string;
  };
}

export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

export interface ResultadoCarga {
  c_vend: string;
  fecha_inicio: Date;
  fecha_fin: Date;
  valor: number;
  estado?: string;
  error?: string;
}

export interface BulkLoadResponse {
  registrosInsertados: number;
  registrosEliminados: number;
  registrosFallidos: ResultadoCarga[];
  mensaje: string;
}

@Injectable({
  providedIn: 'root'
})
export class GarantizadosService {
  ENDPOINT = getEndpoint('coms_garantizados');

  constructor(private http: HttpClient) { }

  getAll(company: string): Observable<ApiResponse<Garantizado[]>> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('company', company);

    return this.http.get<any>(`${this.ENDPOINT}/`, { headers }).pipe(
      map(response => {
        // Si la respuesta ya tiene el formato correcto, la devolvemos
        if (response && response.hasOwnProperty('data')) {
          // Asegurarnos de que los datos estén en el formato correcto
          if (Array.isArray(response.data)) {
            response.data = response.data.map(item => {
              const processed = {
                ...item,
                // Asegurarnos de que nombre_vendedor esté disponible
                nombre_vendedor: item.nombre_vendedor || ''
              };
              return processed;
            });
          }
          return response;
        }

        // Si la respuesta es un array directo, la formateamos
        const formattedData = Array.isArray(response) ? response : [];
        return {
          status: 'success',
          message: 'Garantizados recuperados exitosamente',
          data: formattedData.map(item => ({
            ...item,
            nombre_vendedor: item.nombre_vendedor || ''
          }))
        };
      }),
      catchError((error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse) {
          if (error.headers) {
          }
        }

        let errorMessage = 'Error al obtener garantizados';
        
        if (error.error) {
          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.error) {
            errorMessage = error.error.error;
          }
        }
        
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  create(data: Garantizado, company: string): Observable<ApiResponse<Garantizado>> {
    const currentUser = getCurrentUser();
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('company', company);

    // Agregar el usuario_creacion
    const garantizadoData = {
      ...data,
      usuario_creacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      c_emp: company
    };

    return this.http.post<ApiResponse<Garantizado>>(`${this.ENDPOINT}/`, garantizadoData, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message || 'Error al crear garantizado';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  update(id: number, data: Garantizado, company: string): Observable<ApiResponse<Garantizado>> {
    const currentUser = getCurrentUser();
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('company', company);

    // Agregar el usuario_modificacion y c_emp
    const garantizadoData = {
      ...data,
      usuario_modificacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      c_emp: company
    };

    return this.http.put<ApiResponse<Garantizado>>(`${this.ENDPOINT}/${id}/`, garantizadoData, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message || 'Error al actualizar garantizado';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  delete(id: number, company: string): Observable<any> {
    const currentUser = getCurrentUser();
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('company', company);

    const params = {
      usuario_modificacion: currentUser?.usuario_aplicativo || 'SYSTEM'
    };

    return this.http.delete(`${this.ENDPOINT}/${id}/`, { headers, params }).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message || 'Error al eliminar garantizado';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  bulkLoad(company: string, datos: Garantizado[], reemplazar: boolean): Observable<BulkLoadResponse> {
    const currentUser = getCurrentUser();
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('company', company);
    
    // Agregar c_emp y usuario_creacion a cada registro
    const datosCompletos = datos.map(garantizado => ({
      ...garantizado,
      usuario_creacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      c_emp: currentUser?.c_emp || company // Usar company como fallback
    }));

    const url = reemplazar ? 
      `${this.ENDPOINT}/bulk-load-replace/` : 
      `${this.ENDPOINT}/bulk-load/`;

    const request = {
      datos: datosCompletos,
      usuario: currentUser?.usuario_aplicativo || 'SYSTEM'
    };

    return this.http.post<BulkLoadResponse>(url, request, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.detail || error.message || 'Error al procesar el archivo';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  searchByVendedor(company: string, searchTerm: string): Observable<ApiResponse<Garantizado[]>> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('company', company);

    return this.http.get<ApiResponse<Garantizado[]>>(`${this.ENDPOINT}/search/`, {
      headers,
      params: { search: searchTerm }
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message || 'Error al buscar garantizados';
        return throwError(() => new Error(errorMessage));
      })
    );
  }
}
